import { useCallback, useEffect, useMemo, useState } from "react";
import { admin, create as createApi } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, ArrowUpDown, Sparkles } from "lucide-react";

const fmtAmt = (n) =>
  n == null || n === "" ? "—" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

function PolicyDialog({ open, onOpenChange, existing, candidates, purcTypeOptions, onSaved }) {
  // empty existing => create mode
  const isEdit = !!existing?.id;
  const [name, setName] = useState("");
  const [priority, setPriority] = useState(100);
  const [active, setActive] = useState(true);
  const [purcTypes, setPurcTypes] = useState([]);
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [l1, setL1] = useState("");
  const [l2, setL2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (existing?.id) {
      setName(existing.name || "");
      setPriority(existing.priority ?? 100);
      setActive(existing.active !== false);
      setPurcTypes(existing.purc_types || []);
      setMinAmt(existing.min_amount == null ? "" : String(existing.min_amount));
      setMaxAmt(existing.max_amount == null ? "" : String(existing.max_amount));
      setL1(existing.l1_emp_no || "");
      setL2(existing.l2_emp_no || "");
    } else {
      setName(""); setPriority(100); setActive(true); setPurcTypes([]);
      setMinAmt(""); setMaxAmt(""); setL1(""); setL2("");
    }
  }, [open, existing]);

  const togglePurcType = (t) => {
    setPurcTypes((arr) => (arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]));
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Rule name is required");
    if (!l1) return toast.error("Pick an L1 Authoriser");
    const mn = minAmt === "" ? null : Number(minAmt);
    const mx = maxAmt === "" ? null : Number(maxAmt);
    if (mn != null && mx != null && mn >= mx) return toast.error("Min amount must be less than max amount");
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        priority: Number(priority) || 100,
        active,
        purc_types: purcTypes,
        min_amount: mn,
        max_amount: mx,
        l1_emp_no: l1,
        l2_emp_no: l2 || null,
      };
      if (isEdit) {
        await admin.updatePolicy(existing.id, payload);
        toast.success("Policy updated");
      } else {
        await admin.createPolicy(payload);
        toast.success("Policy created");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle data-testid="policy-dialog-title">{isEdit ? "Edit policy rule" : "New policy rule"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Rule name *</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Service PRs ≥ ₹1L"
                className="mt-1"
                data-testid="policy-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Priority</Label>
              <Input
                type="number"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="mt-1"
                data-testid="policy-priority"
              />
              <div className="text-[10px] text-slate-500 mt-1">Higher = evaluated first</div>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Applies to purchase types</Label>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="policy-purctypes">
              {(purcTypeOptions || []).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => togglePurcType(t)}
                  className={`px-2.5 py-1 text-xs rounded-sm border transition-colors ${
                    purcTypes.includes(t)
                      ? "bg-cyan-700 text-white border-cyan-700"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-slate-500 mt-1">
              {purcTypes.length === 0 ? "No filter — rule applies to ALL purchase types" : `Only: ${purcTypes.join(", ")}`}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Min amount (INR, inclusive)</Label>
              <Input
                type="number"
                value={minAmt}
                onChange={(e) => setMinAmt(e.target.value)}
                placeholder="(no lower bound)"
                className="mt-1"
                data-testid="policy-min"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Max amount (INR, exclusive)</Label>
              <Input
                type="number"
                value={maxAmt}
                onChange={(e) => setMaxAmt(e.target.value)}
                placeholder="(no upper bound)"
                className="mt-1"
                data-testid="policy-max"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">L1 Authoriser *</Label>
              <select
                value={l1}
                onChange={(e) => setL1(e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-sm border border-slate-300 bg-white text-sm"
                data-testid="policy-l1"
              >
                <option value="">— pick employee —</option>
                {candidates.map((c) => (
                  <option key={c.emp_no} value={c.emp_no}>
                    {c.full_name} · {c.emp_no}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">L2 Approver (optional)</Label>
              <select
                value={l2}
                onChange={(e) => setL2(e.target.value)}
                className="mt-1 w-full h-9 px-2 rounded-sm border border-slate-300 bg-white text-sm"
                data-testid="policy-l2"
              >
                <option value="">— single-level (skip L2) —</option>
                {candidates.map((c) => (
                  <option key={c.emp_no} value={c.emp_no}>
                    {c.full_name} · {c.emp_no}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-slate-500 mt-1">Leave empty for single-level approval</div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <div className="flex items-center gap-2">
              <Switch checked={active} onCheckedChange={setActive} data-testid="policy-active" />
              <span className="text-sm">Active</span>
            </div>
            <div className="text-xs text-slate-500">Inactive rules are ignored during routing.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="policy-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="policy-save">
            {saving && <Loader2 className="animate-spin mr-1.5" size={14} />}
            {isEdit ? "Save changes" : "Create rule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApprovalPolicyPanel() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purcTypes, setPurcTypes] = useState([]);
  const [logins, setLogins] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pol, lg, md] = await Promise.all([
        admin.listPolicies(),
        admin.listLogins(),
        createApi.masterData(),
      ]);
      setPolicies(pol.data || []);
      setLogins(lg.data || []);
      setPurcTypes((md.data?.purc_types) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load policies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const candidates = useMemo(
    () => logins.map((l) => ({ emp_no: l.emp_no, full_name: l.full_name || l.username })),
    [logins]
  );

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };

  const remove = async (p) => {
    try {
      await admin.deletePolicy(p.id);
      toast.success("Policy deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const quickToggleActive = async (p) => {
    try {
      await admin.updatePolicy(p.id, { active: !p.active });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="policy-panel">
      <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-3 text-[12px] text-slate-700 flex items-start gap-2">
        <Sparkles size={14} className="mt-0.5 text-cyan-700 flex-shrink-0" />
        <div>
          Rules are evaluated in <b>priority order (high to low)</b>; the first match wins. A rule with no purchase-type filter applies to ALL types. An L2 left blank means <b>single-level approval</b> (L1 stamps both Auth + Approve in one click). Keep at least one active default rule.
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">Approval routing rules</div>
          <div className="text-xs text-slate-500">{policies.length} total &middot; {policies.filter((p) => p.active).length} active</div>
        </div>
        <Button onClick={openCreate} className="bg-cyan-700 hover:bg-cyan-800 text-white" data-testid="add-policy-btn">
          <Plus size={14} className="mr-1" /> Add Rule
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 w-16"><div className="inline-flex items-center gap-1"><ArrowUpDown size={11} /> Pri</div></th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">PR Types</th>
                <th className="text-left px-3 py-2">Amount Range (INR)</th>
                <th className="text-left px-3 py-2">L1 / L2</th>
                <th className="text-center px-3 py-2 w-20">Active</th>
                <th className="text-right px-3 py-2 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500">
                  <Loader2 className="inline animate-spin mr-2" size={14} /> Loading...
                </td></tr>
              )}
              {!loading && policies.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500">No policies configured.</td></tr>
              )}
              {policies.map((p) => (
                <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${p.active ? "" : "opacity-60"}`} data-testid={`policy-row-${p.id}`}>
                  <td className="px-3 py-2 mono font-semibold text-slate-700">{p.priority}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{p.name}</td>
                  <td className="px-3 py-2">
                    {p.purc_types?.length ? (
                      <div className="flex flex-wrap gap-1">
                        {p.purc_types.map((t) => (
                          <span key={t} className="inline-block px-1.5 py-0.5 text-[10px] bg-slate-200 text-slate-700 rounded-sm">{t}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-[11px] text-slate-400 italic">ALL types</span>
                    )}
                  </td>
                  <td className="px-3 py-2 mono text-[12px] text-slate-700">
                    {p.min_amount == null && p.max_amount == null ? (
                      <span className="text-slate-400 italic">any</span>
                    ) : (
                      <>
                        ≥ {fmtAmt(p.min_amount)}
                        {p.max_amount != null && <> &lt; {fmtAmt(p.max_amount)}</>}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[12px]">
                    <div><span className="text-slate-500">L1:</span> {p.l1_name || p.l1_emp_no || "—"} <span className="text-slate-400 mono text-[10px]">{p.l1_emp_no}</span></div>
                    <div>
                      <span className="text-slate-500">L2:</span>{" "}
                      {p.l2_emp_no
                        ? <>{p.l2_name || p.l2_emp_no} <span className="text-slate-400 mono text-[10px]">{p.l2_emp_no}</span></>
                        : <span className="text-slate-400 italic">single-level</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Switch
                      checked={p.active}
                      onCheckedChange={() => quickToggleActive(p)}
                      data-testid={`policy-active-toggle-${p.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`policy-edit-${p.id}`}>
                        <Pencil size={12} className="mr-1" /> Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`policy-delete-${p.id}`}>
                            <Trash2 size={12} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this policy?</AlertDialogTitle>
                            <AlertDialogDescription>
                              <b>{p.name}</b> will be permanently removed. Future PRs matching its criteria will fall through to the next rule.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction className="bg-rose-700 hover:bg-rose-800" onClick={() => remove(p)}>Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PolicyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        existing={editing}
        candidates={candidates}
        purcTypeOptions={purcTypes}
        onSaved={refresh}
      />
    </div>
  );
}
